import Http from '@/services/http';
import serviceFields from '@/domain/data/installation/engineering/coolingEfficiency/serviceFields';
import translate from '@/services/translate';
import invert from 'lodash/invert';
import { grammaticalJoin } from '@/services/arrayUtils';
import {
  CoolingEfficiencyStatus,
  CoolingEfficiencyNorm,
  CoolingEfficiencyCard,
  CalculatedTemperatureResponse,
  CoolingEfficiencyParameters,
  CoolingEfficiencyChart,
  CoolingEfficiencyChartQuery,
  coolingEfficiencyNormsMap,
} from 'sigma-mappings';
import { getOperatorLabel } from '@/services/stringHelper';

/* istanbul ignore next */
const errors = {
  errorHandling: {
    responseMessageParams: {
      $MA001: (data) => {
        const parameters = Object.values(data.parameters) || [];
        return {
          NomeDoCampo: grammaticalJoin(parameters.map(
            field => translate(serviceFields[field]),
          )),
        };
      },
      $MA025: (data) => {
        if (!data) {
          return null;
        }
        const returnedField = serviceFields[data.field];
        return {
          NomeDoCampo: translate(returnedField),
        };
      },
      $MA029: (data) => {
        if (!data && !data.parameters) {
          return null;
        }
        const returnedField = serviceFields[data.parameters[2]];
        return {
          FaixaInicial: data.parameters[0],
          FaixaFinal: data.parameters[1],
          NomeDoCampo: translate(returnedField),
        };
      },
      $MA060: (data) => {
        const parameters = data.parameters || {};
        return {
          field1: translate(serviceFields[parameters[0]]),
          operator: getOperatorLabel(parameters[1]),
          field2: translate(serviceFields[parameters[2]]),
        };
      },
      $MA078: (data) => {
        const parameters = Object.entries(data.parameters);
        const asset = parameters[0][1];
        const variablesArray = parameters
          .slice(1, parameters.length)
          .map(item => item[1]);
        const variables = grammaticalJoin(variablesArray);
        return {
          variables,
          commutator: asset,
        };
      },
      $MA085: data => ({
        variable: data.parameters[0],
      }),
      $MA092: (data) => {
        if (!data && !data.parameters) {
          return null;
        }
        const returnedField = serviceFields[data.parameters[0]];
        return {
          0: translate(returnedField),
          1: data.parameters[1],
        };
      },
    },
  },
};

const urls = {
   status: assetModuleId => `/engenharia/${assetModuleId}/eficiencia-resfriamento/status`,
   norms: assetModuleId => `/engenharia/${assetModuleId}/eficiencia-resfriamento/normas`,
   norm: (assetModuleId, normId) => `/engenharia/${assetModuleId}/eficiencia-resfriamento/${normId}`,
   calculatedTemperature: (assetModuleId, normId) => `/engenharia/${assetModuleId}/eficiencia-resfriamento/${normId}/temperatura-calculada`,
   parameters: assetModuleId => `/engenharia/${assetModuleId}/eficiencia-resfriamento/parametros`,
   chart: assetModuleId => `/engenharia/${assetModuleId}/eficiencia-resfriamento/grafico`,
};

export default {
  getStatus(assetModuleId) {
    const http = new Http(errors);
    return http.get(urls.status(assetModuleId))
      .then(({ data }) => new CoolingEfficiencyStatus(data));
  },
  getNorms(assetModuleId) {
    const http = new Http(errors);
    return http.get(urls.norms(assetModuleId))
      .then(({ data }) => data.map(item => new CoolingEfficiencyNorm(item)));
  },
  getCardsByNorm(assetModuleId, normId) {
    const http = new Http(errors);
    const norm = invert(coolingEfficiencyNormsMap)[normId];
    return http.get(urls.norm(assetModuleId, norm))
      .then(({ data }) => data.map(item => new CoolingEfficiencyCard(item)));
  },
  getCalculatedTemperature(assetModuleId, normId) {
    const http = new Http(errors);
    const norm = invert(coolingEfficiencyNormsMap)[normId];
    return http.get(urls.calculatedTemperature(assetModuleId, norm))
      .then(({ data }) => new CalculatedTemperatureResponse(data));
  },
  getParameters(assetModuleId) {
    const http = new Http(errors);
    return http.get(urls.parameters(assetModuleId))
      .then(({ data }) => new CoolingEfficiencyParameters(data));
  },
  putParameters(assetModuleId, payload) {
    const http = new Http(errors);
    const mappedPayload = new CoolingEfficiencyParameters.MapBack(payload);
    return http.put(urls.parameters(assetModuleId), mappedPayload);
  },
  getChart(assetModuleId, chartFilters) {
    const http = new Http(errors);
    const chartQuery = new CoolingEfficiencyChartQuery(chartFilters);
    return http.get(urls.chart(assetModuleId), { params: chartQuery })
      .then(({ data }) => data.map(item => new CoolingEfficiencyChart(item)));
  },
};
